import { buildPermission } from "common/utils/auth";
import { Button, MenuItem, Select } from "@mui/material";
import { classes, SELECT_DARK } from "portal/theme/theme";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { portalApi } from "portal/state/portalApi";
import {
  ShapefileBundleWizardProps,
  ShapefileLoadingState,
  WizardStep,
} from "../Wizard";
import { shapefileImportWorker } from "portal/workers";
import { ShpToGeojsonConvertRequest } from "../workerApi";
import { Trans, useTranslation } from "react-i18next";
import { useAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import FileOpenIcon from "@mui/icons-material/FileOpen";
import React, { ChangeEventHandler } from "react";

export const SelectFile: WizardStep<ShapefileBundleWizardProps> = {
  stepNameI18NKey: "views.farms.importFarms.step1SelectFile.stepName",
  Render: ({
    customerId,
    setCustomerId,
    resetShapefile,
    shapefileLoadingState,
    nImportableFields,
  }) => {
    const { t } = useTranslation();
    const canUpdateAllFarms = useAuthorizationRequired(
      buildPermission(
        PermissionAction.update,
        PermissionResource.farms,
        PermissionDomain.all
      )
    );

    const { data: customers, isLoading } = useQueryPopups(
      portalApi.endpoints.listCustomers.useQuery(undefined, {
        skip: !canUpdateAllFarms,
      })
    );

    const onFileChange: ChangeEventHandler<HTMLInputElement> = async (e) => {
      const file = e.target.files?.item(0);

      if (!file) {
        resetShapefile();
        return;
      }

      shapefileImportWorker.postMessage({
        type: "shp-convert-req",
        arrayBuffer: await file.arrayBuffer(),
      } satisfies ShpToGeojsonConvertRequest);
    };

    const displaySuccess =
      shapefileLoadingState === ShapefileLoadingState.Ok &&
      nImportableFields > 0;
    const displayError =
      shapefileLoadingState === ShapefileLoadingState.Error ||
      (shapefileLoadingState === ShapefileLoadingState.Ok &&
        nImportableFields === 0);

    return (
      <>
        {canUpdateAllFarms && (
          <>
            <div>
              {t("views.farms.importFarms.step1SelectFile.chooseCustomer")}
            </div>
            <Select
              {...SELECT_DARK}
              className={classes(SELECT_DARK.className, "w-full")}
              disabled={isLoading}
              defaultValue={customerId}
              onChange={(e) => setCustomerId(e.target.value as number)}
            >
              {customers?.map(({ db, name }) => (
                <MenuItem value={db?.id} key={String(db?.id)}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </>
        )}
        <div>
          <Trans
            i18nKey="views.farms.importFarms.step1SelectFile.shapefilesIntro"
            t={t}
            components={{
              // eslint-disable-next-line jsx-a11y/anchor-has-content
              kblink: <a href="/knowledge" className="underline" />,
            }}
          />
        </div>
        <Button
          component="label"
          role={undefined}
          variant="contained"
          tabIndex={-1}
          startIcon={<FileOpenIcon />}
        >
          {t("views.farms.importFarms.step1SelectFile.chooseButton")}
          <input
            hidden={true}
            type="file"
            accept=".zip,application/zip"
            onChange={onFileChange}
          />
        </Button>
        {displaySuccess && (
          <div className="text-success">
            {t("views.farms.importFarms.step1SelectFile.fieldsFound", {
              count: nImportableFields,
            })}
          </div>
        )}
        {displayError && (
          <div className="text-error">
            {t("views.farms.importFarms.step1SelectFile.fieldsNotFound")}
          </div>
        )}
      </>
    );
  },
  mapSelectionEnabled: false,
  canAdvancePastStep: ({ customerId, nImportableFields, shapefile }) =>
    shapefile.length > 0 && nImportableFields > 0 && Boolean(customerId),
};
